import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { DurableStorage } from '../plugins/durable-storage.plugin';
import { DURABLE_STORAGE_KEYS, StorageKeys } from './storage-keys';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  /**
   * Set item using Capacitor Preferences (works on native and web)
   */
  async setItem<T>(key: string, value: T): Promise<void> {
    const serialized = JSON.stringify(value);

    try {
      await this.setSerialized(key, serialized);
    } catch (error) {
      console.error(`Failed to set storage item "${key}":`, error);
      throw error;
    }
  }

  /**
   * Get item using Capacitor Preferences (works on native and web)
   */
  async getItem<T>(key: string, defaultValue?: T): Promise<T | null> {
    try {
      const itemStr = await this.getSerialized(key);

      if (!itemStr) {
        return defaultValue !== undefined ? defaultValue : null;
      }

      return JSON.parse(itemStr) as T;
    } catch (error) {
      console.error(`Failed to get storage item "${key}":`, error);
      return defaultValue !== undefined ? defaultValue : null;
    }
  }

  /**
   * Read a durable workflow record without converting corruption or an I/O
   * failure into "not found". Payment and print recovery must fail closed.
   */
  async getItemStrict<T>(key: string): Promise<T | null> {
    const value = await this.getSerialized(key);
    if (value === null) {
      return null;
    }
    return JSON.parse(value) as T;
  }

  /**
   * Remove item
   */
  async removeItem(key: string): Promise<void> {
    try {
      await this.removeSerialized(key);
    } catch (error) {
      console.error(`Failed to remove storage item "${key}":`, error);
    }
  }

  /** Remove a durable workflow record and propagate a storage failure. */
  async removeItemStrict(key: string): Promise<void> {
    await this.removeSerialized(key);
  }

  /**
   * Clear all storage
   */
  async clear(): Promise<void> {
    try {
      await Preferences.clear();
    } catch (error) {
      console.error('Failed to clear storage:', error);
          }
  }

  /**
   * Get all keys
   */
  async keys(): Promise<string[]> {
    try {
      const result = await Preferences.keys();
      return result.keys;
    } catch (error) {
      console.error('Failed to get storage keys:', error);
      return [];
    }
  }

  /**
   * Check if storage is available
   */
  async isStorageAvailable(): Promise<boolean> {
    try {
      const testKey = '__storage_test__';
      await this.setItem(testKey, 'test');
      await this.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage information
   */
  async getStorageInfo(): Promise<{
    used: number;
    available: number;
    percentage: number;
  }> {
    try {
      const keys = await this.keys();
      let used = 0;

      for (const key of keys) {
        const result = await Preferences.get({ key });
        const itemStr = result.value;

        if (itemStr) {
          used += key.length + itemStr.length;
        }
      }

      // Capacitor Preferences typically has 100MB limit
      const available = 100 * 1024 * 1024; // 100MB
      const percentage = (used / available) * 100;

      return { used, available, percentage };
    } catch {
      return { used: 0, available: 0, percentage: 0 };
    }
  }

  /**
   * Clear only translation-related items
   */
  async clearTranslationItems(): Promise<void> {
    try {
      const translationKeys = [
        'user_language',
        'language_change_timestamp',
        'translation_cache',
        'last_translation_load',
        'selected_language',
        'translation_performance',
        'translation_debug',
        'cached_translations',
        'translation_version',
      ];

      const translationPrefixes = ['translation_', 'lang_', 'locale_', 'i18n_'];
      const allKeys = await this.keys();
      const keysToRemove: string[] = [];

      for (const key of allKeys) {
        if (translationKeys.includes(key)) {
          keysToRemove.push(key);
        } else if (
          translationPrefixes.some((prefix) => key.startsWith(prefix))
        ) {
            keysToRemove.push(key);
          }
      }

      for (const key of keysToRemove) {
        await this.removeItem(key);
      }
    } catch (error) {
      console.error('Failed to clear translation items:', error);
    }
  }

  /**
   * Get all translation-related keys
   */
  async getTranslationKeys(): Promise<string[]> {
    try {
      const translationKeys = [
        'user_language',
        'language_change_timestamp',
        'translation_cache',
        'last_translation_load',
        'selected_language',
        'translation_performance',
        'translation_debug',
        'cached_translations',
        'translation_version',
      ];

      const foundKeys: string[] = [];
      const translationPrefixes = ['translation_', 'lang_', 'locale_', 'i18n_'];
      const allKeys = await this.keys();

      for (const key of allKeys) {
        if (translationKeys.includes(key)) {
          foundKeys.push(key);
        } else if (
          translationPrefixes.some((prefix) => key.startsWith(prefix))
        ) {
            foundKeys.push(key);
        }
      }

      return foundKeys;
    } catch (error) {
      console.error('Failed to get translation keys:', error);
      return [];
    }
  }

  private isAndroidDurableKey(key: string): boolean {
    return (
      Capacitor.getPlatform() === 'android' &&
      (DURABLE_STORAGE_KEYS as readonly string[]).includes(key)
    );
  }

  private async setSerialized(key: string, value: string): Promise<void> {
    if (this.isAndroidDurableKey(key)) {
      await DurableStorage.set({ key, value });
      return;
    }
    await Preferences.set({ key, value });
  }

  private async getSerialized(key: string): Promise<string | null> {
    if (this.isAndroidDurableKey(key)) {
      return (await DurableStorage.get({ key })).value;
    }
    return (await Preferences.get({ key })).value;
  }

  private async removeSerialized(key: string): Promise<void> {
    if (this.isAndroidDurableKey(key)) {
      await DurableStorage.remove({ key });
      return;
    }
    await Preferences.remove({ key });
  }
}
